import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Dialog, Button, DialogActions, DialogTitle, DialogContent } from '@mui/material'

import { checkIpRange, extendSession, SESSION_EXPIRY_WARN_THRESHOLD } from 'store/actions/user'
import { selectSession } from 'store/selectors/user'

const SessionTimeoutModal = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const dispatch = useDispatch()

  const { expiresIn, hasExpired, isRemote } = useSelector(selectSession)

  const [isOpen, setIsOpen] = useState(false)
  const [timeLeft, setTimeLeft] = useState(-1)

  const intervalRef = useRef(null)

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const dismiss = () => {
    clearTimer()
    setIsOpen(false)
    setTimeLeft(-1)
  }

  useEffect(() => {
    if (!isRemote) {
      return
    } else if (expiresIn < 0) {
      return
    } else if (expiresIn > SESSION_EXPIRY_WARN_THRESHOLD) {
      dismiss()
      return
    }

    setIsOpen(true)
    setTimeLeft(expiresIn)

    if (!intervalRef.current) {
      // Update remaining time live
      intervalRef.current = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime == 0) {
            clearTimer()
          }
          return prevTime - 1
        })
      }, 1000)
    }

    return clearTimer
  }, [expiresIn])

  // Open modal when session expires
  useEffect(() => {
    if (hasExpired) setIsOpen(true)
  }, [hasExpired])

  const modalTextBundle = t.global.sessionTimeoutModal
  const modalTextType = isRemote ? modalTextBundle.remote : modalTextBundle.normal
  const modalText = hasExpired ? modalTextType.expired : modalTextType.expiring

  const onAction = () => {
    if (isRemote) {
      if (hasExpired) {
        dispatch(checkIpRange())
      } else {
        dispatch(extendSession())
      }
    }
    dismiss()
  }

  return (
    <Dialog className="SessionTimeoutDialog Dialog" open={isOpen} onClose={dismiss}>
      <DialogTitle>{modalText.title}</DialogTitle>
      <DialogContent>
        <p>{modalText.body.replace('{seconds}', Math.max(timeLeft, 0))}</p>
      </DialogContent>
      <DialogActions>
        <Button disabled={false} onClick={onAction} variant="text">
          {modalText.action}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SessionTimeoutModal
